import { Colors } from "@eai-toolkit-material-ui/theme";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import type { TextFieldProps } from "@mui/material";
import { Button, MenuItem, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AutocompleteWithWrap } from "@components";
import { ValidationTextField } from "@components/ValidationContext/ValidationContext";

import { LOCAL_STORAGE_KEY_RECENTLY_CHOSEN_IMAGES } from "@helpers/constants";

import { DockerIcon, EaiIcon } from "../../../icons";
import {
  useCheckDockerHubImageExistsQuery,
  useCheckToolkitImageExistsQuery,
  useGetDockerHubLatestImageTagsQuery,
  useGetToolkitImageRepositoriesQuery,
  useGetToolkitImageTagsQuery,
  useGetToolkitRegistriesQuery,
  useLazySearchDockerHubQuery,
} from "../../../store/api/imagesApi";
import {
  parseDockerImage,
  stringifyDockerImage,
  validateImage,
} from "../utils";
import { fieldStyle, wideFieldStyle } from "./NewJobFormLayout";

function getRecents(): string[] | null {
  const str = localStorage.getItem(LOCAL_STORAGE_KEY_RECENTLY_CHOSEN_IMAGES);
  return str ? str.split(",").slice(0, 3) : null;
}

function addRecent(image: string) {
  const recents = getRecents() ?? [];
  localStorage.setItem(
    LOCAL_STORAGE_KEY_RECENTLY_CHOSEN_IMAGES,
    [image]
      .concat(recents.filter((r) => r !== image))
      .slice(0, 3)
      .join(","),
  );
}

const RecentImage = styled(Button)<{ selected?: boolean }>(
  ({ theme, selected }) => ({
    ...fieldStyle,
    height: theme.spacing(7),
    backgroundColor: theme.palette.grey[200],
    "&:hover": {
      backgroundColor: theme.palette.grey[300],
    },
    ...(selected && {
      backgroundColor: Colors.purple[50],
      borderColor: Colors.purple[200],
    }),
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    "& p": {
      lineHeight: 1.3,
      whiteSpace: "nowrap",
      maxWidth: "100%",
      overflow: "hidden",
      textAlign: "left",
      textOverflow: "ellipsis",
    },
  }),
);

const SelectIcon = styled("div")(({ theme }) => ({
  "& > svg": {
    height: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
}));

type Props = {
  value: string;
  onUpdate: (value: string) => void;
  submitted: boolean;
};

const ImageField = React.memo(function ImageField({
  value,
  onUpdate,
  submitted,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const [manualEntry, setManualEntry] = useState(
    Boolean(location.state?.manualImageEntry),
  );

  useEffect(() => {
    const currentState = location.state || {};
    if (currentState.manualImageEntry !== manualEntry) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {
          ...currentState,
          manualImageEntry: manualEntry,
        },
      });
    }
  }, [navigate, manualEntry, location]);

  const hasRenderedWithoutManualEntry = useRef(false);
  if (!manualEntry) {
    hasRenderedWithoutManualEntry.current = true;
  }

  const { registry, repository, tag } = parseDockerImage(value);

  const recentImageRef = useRef(value);
  useEffect(() => {
    recentImageRef.current = value;
  }, [value]);
  const [recentImages, setRecentImages] = useState(getRecents());
  useEffect(() => {
    if (submitted && !validateImage(recentImageRef.current)) {
      addRecent(recentImageRef.current);
      setRecentImages(getRecents());
    }
  }, [submitted]);

  const { data: toolkitRegistryNames } = useGetToolkitRegistriesQuery();

  const [repositoryInputValue, setRepositoryInputValue] = useState(repository);
  const [tagInputValue, setTagInputValue] = useState(tag);

  const registryKey = registry.startsWith("volatile-registry.")
    ? "volatileRegistry"
    : registry.startsWith("registry.")
      ? "registry"
      : "dockerHub";

  const isVolatile = registryKey === "volatileRegistry";
  const isDockerHub = registryKey === "dockerHub";

  const { data: toolkitImageExists } = useCheckToolkitImageExistsQuery(
    { repositoryName: repository, volatile: isVolatile },
    { skip: !repository || isDockerHub },
  );
  const { data: dockerHubImageExists } = useCheckDockerHubImageExistsQuery(
    { repositoryName: repository },
    { skip: !repository || !isDockerHub },
  );

  const repositoryExistsStatus = isDockerHub
    ? dockerHubImageExists
    : toolkitImageExists;

  const { data: toolkitTags } = useGetToolkitImageTagsQuery(
    { repositoryName: repository, volatile: isVolatile },
    { skip: !repository || isDockerHub },
  );
  const { data: dockerHubTags } = useGetDockerHubLatestImageTagsQuery(
    { repositoryName: repository },
    { skip: !repository || !isDockerHub },
  );

  const tagOptions = isDockerHub ? dockerHubTags : toolkitTags;

  const { data: toolkitRepositories } = useGetToolkitImageRepositoriesQuery(
    { volatile: isVolatile },
    { skip: isDockerHub },
  );

  const [awaitingAutoTagFill, setAwaitingAutoTagFill] = useState(false);

  useEffect(() => {
    if (!awaitingAutoTagFill) {
      return;
    }
    if (tag) {
      setAwaitingAutoTagFill(false);
      return;
    }
    if (tagOptions && tagOptions.length) {
      onUpdate(
        stringifyDockerImage({
          registry,
          repository,
          tag: tagOptions[0],
        }),
      );
      setAwaitingAutoTagFill(false);
    }
  }, [awaitingAutoTagFill, registry, repository, tag, tagOptions, onUpdate]);

  // Currently this searches on every keystroke - in the future we may want to debounce
  const [searchDockerHub, { data: dockerHubSearchResults }] =
    useLazySearchDockerHubQuery();
  const freshDockerHubRepoSearches = useRef<string[]>([]);
  const [dockerHubRepoResults, setDockerHubRepoResults] = useState<
    string[] | null
  >(null);
  useEffect(() => {
    if (dockerHubSearchResults) {
      setDockerHubRepoResults(dockerHubSearchResults);
    }
  }, [dockerHubSearchResults]);

  const handleRepositoryInputChange = useCallback(
    (_: unknown, repositoryInput: string) => {
      // docker hub search doesn't work for single characters
      if (isDockerHub && repositoryInput.length > 1) {
        freshDockerHubRepoSearches.current =
          freshDockerHubRepoSearches.current.concat(repositoryInput);
        searchDockerHub({ searchString: repositoryInput }).then(() => {
          if (!freshDockerHubRepoSearches.current.includes(repositoryInput)) {
            return;
          }
          // don't allow searches older than this one to populate the results list
          freshDockerHubRepoSearches.current =
            freshDockerHubRepoSearches.current.slice(
              freshDockerHubRepoSearches.current.lastIndexOf(repositoryInput) +
                1,
            );
        });
      } else {
        freshDockerHubRepoSearches.current = [];
        setDockerHubRepoResults(null);
      }
      setRepositoryInputValue(repositoryInput);
    },
    [isDockerHub, searchDockerHub],
  );

  const handleTagInputChange = useCallback(
    (_: unknown, tagInput: React.SetStateAction<string>) => {
      setTagInputValue(tagInput);
    },
    [],
  );

  const registryOptions = useMemo(() => {
    const options = [
      { value: "dockerHub", label: "Docker Hub", Icon: DockerIcon },
      {
        value: toolkitRegistryNames?.registry,
        label: toolkitRegistryNames?.registry ?? "registry",
        Icon: EaiIcon,
      },
      {
        value: toolkitRegistryNames?.volatileRegistry,
        label: toolkitRegistryNames?.volatileRegistry ?? "volatile-registry",
        Icon: EaiIcon,
      },
    ];
    return options.map(({ value, label, Icon }, index) => (
      <MenuItem key={index} value={value} disabled={!value}>
        <SelectIcon>
          <Icon />
          {label}
        </SelectIcon>
      </MenuItem>
    ));
  }, [toolkitRegistryNames]);

  const repositoryOptions = isDockerHub
    ? dockerHubRepoResults
    : toolkitRepositories;

  const handleRegistryChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (e) => {
        const registry = e.target.value;
        onUpdate(
          stringifyDockerImage({
            registry: registry === "dockerHub" ? "" : registry,
            repository: "",
            tag: "",
          }),
        );
      },
      [onUpdate],
    );

  const handleRepositoryChange = useCallback(
    (_: any, repository: string | null) => {
      onUpdate(
        stringifyDockerImage({
          registry,
          repository: repository ?? "",
          tag: "",
        }),
      );
      setAwaitingAutoTagFill(true);
    },
    [registry, onUpdate],
  );

  const handleRepositoryInputBlur = useCallback(() => {
    if (repository !== repositoryInputValue) {
      handleRepositoryChange(undefined, repositoryInputValue);
    }
  }, [repository, repositoryInputValue, handleRepositoryChange]);

  const handleTagChange = useCallback(
    (_: any, tag: string | null) => {
      onUpdate(stringifyDockerImage({ registry, repository, tag: tag ?? "" }));
    },
    [registry, repository, onUpdate],
  );

  const handleTagInputBlur = useCallback(() => {
    if (tag !== tagInputValue) {
      handleTagChange(undefined, tagInputValue);
    }
  }, [tag, tagInputValue, handleTagChange]);

  const handleFullImageNameChange: React.FocusEventHandler<HTMLInputElement> =
    useCallback(
      (e) => {
        onUpdate(e.target.value);
      },
      [onUpdate],
    );

  const handleFullImageInputFocus: React.FocusEventHandler<HTMLInputElement> =
    useCallback((e) => {
      e.target.select();
    }, []);

  const registryError =
    registry &&
    toolkitRegistryNames &&
    !Object.values(toolkitRegistryNames).includes(registry)
      ? "Registry selection required"
      : null;
  const repositoryError = !repository
    ? "Image selection required"
    : repository !== repositoryInputValue
      ? null
      : repositoryExistsStatus === "no"
        ? "Image does not appear to exist"
        : repositoryExistsStatus === "empty"
          ? "No tags found for image"
          : null;
  const tagError =
    !tag && !tagInputValue && tagOptions ? "Tag selection required" : null;
  const fullImageNameError = validateImage(value) ?? null;

  return (
    <Stack spacing={2} sx={{ opacity: manualEntry ? 0.5 : undefined }}>
      <Stack
        spacing={2}
        sx={{ pointerEvents: manualEntry ? "none" : undefined }}
      >
        {recentImages && (
          <>
            <Typography variant="subtitle2" color="textSecondary">
              Recently used
            </Typography>
            <Stack direction="column" spacing={1}>
              {recentImages.map((image) => {
                const { registry, repository, tag } = parseDockerImage(image);
                const localImage = stringifyDockerImage({
                  registry: "",
                  repository,
                  tag,
                });
                return (
                  <RecentImage
                    key={image}
                    variant="outlined"
                    selected={value === image}
                    onClick={() => onUpdate(image)}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {registry || "Docker Hub"}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      color="textPrimary"
                      component="p"
                    >
                      {localImage}
                    </Typography>
                  </RecentImage>
                );
              })}
            </Stack>
          </>
        )}
        <ValidationTextField
          instanceId="image:registry"
          select
          required
          inputProps={{ disabled: manualEntry }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          label="Registry"
          value={registry || "dockerHub"}
          valueForValidation={registry}
          onChange={handleRegistryChange}
          error={manualEntry ? null : registryError}
          sx={wideFieldStyle}
        >
          {registryOptions}
        </ValidationTextField>
        <Stack direction="row" spacing={1} sx={wideFieldStyle}>
          <AutocompleteWithWrap
            freeSolo
            autoComplete
            autoHighlight
            forcePopupIcon
            fullWidth
            sx={fieldStyle}
            loading={!repositoryOptions}
            loadingText={
              registryKey === "dockerHub"
                ? "Start typing to search"
                : "Loading..."
            }
            options={repositoryOptions ?? []}
            value={repository}
            onChange={handleRepositoryChange}
            onInputChange={handleRepositoryInputChange}
            onBlur={handleRepositoryInputBlur}
            renderInput={(props: TextFieldProps) => (
              <ValidationTextField
                {...props}
                instanceId="image:repository"
                required
                inputProps={{ ...props.inputProps, disabled: manualEntry }}
                label="Image"
                variant="outlined"
                valueForValidation={repository}
                error={manualEntry ? null : repositoryError}
              />
            )}
          />
          <AutocompleteWithWrap
            freeSolo
            autoComplete
            autoHighlight
            forcePopupIcon
            fullWidth
            sx={fieldStyle}
            loading={!tagOptions}
            loadingText={
              repository ? "Loading..." : "Choose image to see suggestions"
            }
            options={tagOptions ?? []}
            value={tag}
            onChange={handleTagChange}
            onInputChange={handleTagInputChange}
            onBlur={handleTagInputBlur}
            renderInput={(props: TextFieldProps) => (
              <ValidationTextField
                {...props}
                instanceId="image:tag"
                required
                inputProps={{ ...props.inputProps, disabled: manualEntry }}
                label="Tag"
                variant="outlined"
                valueForValidation={tag}
                error={manualEntry ? null : tagError}
                validateOthersOnBlur={["image:repository"]}
              />
            )}
          />
        </Stack>
      </Stack>
      <Button
        color="primary"
        onClick={() => setManualEntry((manualEntry) => !manualEntry)}
        sx={{ alignSelf: "flex-start", pointerEvents: "auto" }}
      >
        {manualEntry ? <KeyboardArrowUp /> : <KeyboardArrowDown />} Manually
        paste an image
      </Button>
      {manualEntry && (
        <ValidationTextField
          instanceId="image:fullname"
          required
          autoFocus={hasRenderedWithoutManualEntry.current}
          onFocus={handleFullImageInputFocus}
          label="Full image name"
          variant="outlined"
          defaultValue={repository ? value : ""}
          valueForValidation={repository ? value : ""}
          onBlur={handleFullImageNameChange}
          error={fullImageNameError}
          sx={wideFieldStyle}
        />
      )}
    </Stack>
  );
});

export default ImageField;
